import { useQueries, useQuery } from '@tanstack/react-query';

import type {
  CampaignClient,
  CampaignDetailDto,
} from '@/api/campaign-client';
import { useCampaignClient } from '@/api/campaign-client-context';
import {
  campaignDetailKey,
  campaignsListKey,
} from '@/api/use-campaigns';

// =============================================================================
// use-prior-eligible-campaigns.ts — Résolution FRONT des campagnes antérieures éligibles (V-ANALYSIS-COMPARE).
// =============================================================================
// Décision ORCH (réserve ARCH levée) : la résolution des campagnes antérieures éligibles se fait CÔTÉ FRONT
// (ou campaign BC) — JAMAIS de jointure cross-schéma analytics↔campaign (invariant n°2). Éligibilité =
//   • même clientSiren (au moins un ClientScope partagé avec la campagne COURANTE), ET
//   • state === 'Publiee' (campagne terminée), ET
//   • campaignId != courant (jamais se comparer à soi).
// `catalogVersionId` n'est PAS une clé de périmètre : c'est un DRAPEAU de comparabilité, remonté ici pour que
// le panneau l'affiche (comparable ⇔ == catalogue courant). ZÉRO règle métier serveur ici : le front assemble
// des lectures campaign existantes (liste + détails) déjà autorisées (AdminSessionGuard) et déduit la liste.
//
// PERF : les détails candidats ne sont lus QUE lorsque `enabled` est vrai (panneau ouvert) ET une fois la
// liste + le détail courant résolus. Les queryKeys réutilisent celles de use-campaigns ⇒ cache partagé.

/** Une campagne antérieure éligible, avec son drapeau de comparabilité (catalogue). */
export interface EligiblePriorCampaign {
  readonly campaignId: string;
  readonly intitule: string;
  readonly catalogVersionId: string;
}

/** Résultat de la résolution : campagnes éligibles + catalogue courant (référence de comparabilité). */
export interface PriorEligibleCampaignsResult {
  readonly eligible: readonly EligiblePriorCampaign[];
  readonly currentCatalogVersionId: string | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
}

/**
 * Résout, côté front, les campagnes antérieures éligibles à la comparaison pour `campaignId`. `enabled` gate
 * toutes les lectures (panneau optionnel masquable). Renvoie la liste éligible (même clientSiren, Publiee,
 * hors courante) triée par intitulé, plus le `catalogVersionId` de la campagne courante.
 */
export function usePriorEligibleCampaigns(
  campaignId: string,
  enabled: boolean,
): PriorEligibleCampaignsResult {
  const client: CampaignClient = useCampaignClient();

  // Détail de la campagne COURANTE : clientSirens + catalogue courant (référence de comparabilité).
  const currentQuery = useQuery<CampaignDetailDto, Error>({
    queryKey: campaignDetailKey(campaignId),
    queryFn: () => client.getCampaign(campaignId),
    enabled,
  });

  // Liste de TOUTES les campagnes : on ne garde que les candidates Publiee (hors courante).
  const listQuery = useQuery({
    queryKey: campaignsListKey,
    queryFn: () => client.listCampaigns(),
    enabled,
  });

  const currentSirens = new Set(
    (currentQuery.data?.clientScopes ?? []).map((s) => s.clientSiren),
  );
  const candidates = (listQuery.data ?? []).filter(
    (c) => c.state === 'Publiee' && c.campaignId !== campaignId,
  );

  // Détails des candidates — lus SEULEMENT une fois liste + courant résolus (pour connaître leur clientSiren).
  const detailsReady =
    enabled && currentQuery.isSuccess && listQuery.isSuccess;
  const candidateDetails = useQueries({
    queries: candidates.map((c) => ({
      queryKey: campaignDetailKey(c.campaignId),
      queryFn: () => client.getCampaign(c.campaignId),
      enabled: detailsReady,
    })),
  });

  // Éligibles = candidates dont AU MOINS un ClientScope est partagé avec la campagne courante.
  const eligible: EligiblePriorCampaign[] = candidateDetails
    .map((q) => q.data)
    .filter((d): d is CampaignDetailDto => d !== undefined)
    .filter((d) =>
      d.clientScopes.some((s) => currentSirens.has(s.clientSiren)),
    )
    .map((d) => ({
      campaignId: d.campaignId,
      intitule: d.intitule,
      catalogVersionId: d.catalogVersionId,
    }))
    .sort((a, b) => a.intitule.localeCompare(b.intitule));

  const isPending =
    enabled &&
    (currentQuery.isPending ||
      listQuery.isPending ||
      (detailsReady && candidateDetails.some((q) => q.isPending)));
  const isError =
    currentQuery.isError ||
    listQuery.isError ||
    candidateDetails.some((q) => q.isError);

  return {
    eligible,
    currentCatalogVersionId: currentQuery.data?.catalogVersionId,
    isPending,
    isError,
  };
}
